import type { RightPanelSurface } from "~/rightPanelStore";
import { lazy, Suspense } from "react";
import {
  createRightPanelSurfaceDefinitions,
  type RightPanelSurfaceRenderers,
} from "~/rightPanelSurfaceDefinitions";
import {
  WorkspaceSurfaceHost,
  workspaceSurfaceResourceOwnerIdentity,
  type WorkspaceSurfaceHostProps,
} from "~/workspaceSurfaceHost";

import {
  BrowserWorkspaceSurface,
  BrowserWorkspaceSurfaceProvider,
  type BrowserWorkspaceSurfaceContextValue,
} from "./browser/BrowserWorkspaceSurface";
import {
  DiffWorkspaceSurfaceProvider,
  type DiffWorkspaceSurfaceContextValue,
} from "./diffs/DiffWorkspaceSurfaceContext";
import {
  FilesWorkspaceSurface,
  FilesWorkspaceSurfaceProvider,
  type FilesWorkspaceSurfaceContextValue,
} from "./files/FilesWorkspaceSurface";
import { ThreadPullRequestWorkspaceSurface } from "./pullRequest/ThreadPullRequestWorkspaceSurface";
import {
  RightPanelSurfaceContentProvider,
  type RightPanelSurfaceContentMap,
} from "./RightPanelSurfaceContent";
import {
  TerminalWorkspaceSurface,
  TerminalWorkspaceSurfaceProvider,
  type TerminalWorkspaceSurfaceContextValue,
} from "./terminals/TerminalWorkspaceSurface";

const DiffWorkspaceSurface = lazy(() =>
  import("./diffs/DiffWorkspaceSurface").then((module) => ({
    default: module.DiffWorkspaceSurface,
  })),
);

const WorkflowTabSurface = lazy(() =>
  import("./workflow/WorkflowTabSurface").then((module) => ({
    default: module.WorkflowTabSurface,
  })),
);

const ExperimentTabSurface = lazy(() =>
  import("./experiment/ExperimentTabSurface").then((module) => ({
    default: module.ExperimentTabSurface,
  })),
);

const renderFilesWorkspaceSurface: NonNullable<RightPanelSurfaceRenderers["files"]> = ({
  descriptor,
  renderRegion,
}) => renderRegion(<FilesWorkspaceSurface descriptor={descriptor} />);

/**
 * The production renderer inventory for right-panel surfaces a feature draws itself. A kind absent
 * here falls to `renderRightPanelRegion`, which renders registered content instead.
 */
export const RIGHT_PANEL_WORKSPACE_SURFACE_DEFINITIONS = createRightPanelSurfaceDefinitions({
  preview: ({ descriptor, renderRegion }) =>
    renderRegion(<BrowserWorkspaceSurface descriptor={descriptor} />),
  diff: ({ descriptor, renderRegion, scope }) =>
    renderRegion(
      <Suspense fallback={null}>
        <DiffWorkspaceSurface
          descriptor={descriptor}
          ownerIdentity={workspaceSurfaceResourceOwnerIdentity(scope, descriptor)}
        />
      </Suspense>,
    ),
  file: renderFilesWorkspaceSurface,
  files: renderFilesWorkspaceSurface,
  terminal: ({ descriptor, renderRegion }) =>
    renderRegion(<TerminalWorkspaceSurface descriptor={descriptor} />),
  "pull-request": ({ descriptor, renderRegion, scope }) =>
    renderRegion(<ThreadPullRequestWorkspaceSurface descriptor={descriptor} scope={scope} />),
  workflow: ({ descriptor, renderRegion, scope }) =>
    renderRegion(
      <Suspense fallback={null}>
        <WorkflowTabSurface descriptor={descriptor} scope={scope} />
      </Suspense>,
    ),
  experiment: ({ descriptor, renderRegion, scope }) =>
    renderRegion(
      <Suspense fallback={null}>
        <ExperimentTabSurface descriptor={descriptor} scope={scope} />
      </Suspense>,
    ),
});

export interface RightPanelWorkspaceSurfaceHostProps extends Omit<
  WorkspaceSurfaceHostProps<RightPanelSurface>,
  "definitions"
> {
  readonly browserContext: BrowserWorkspaceSurfaceContextValue | null;
  readonly diffContext: DiffWorkspaceSurfaceContextValue | null;
  readonly filesContext: FilesWorkspaceSurfaceContextValue | null;
  readonly surfaceContent: RightPanelSurfaceContentMap;
  readonly terminalContext: TerminalWorkspaceSurfaceContextValue | null;
}

/** Compose the production host with every feature-owned renderer boundary. */
export function RightPanelWorkspaceSurfaceHost({
  browserContext,
  diffContext,
  filesContext,
  surfaceContent,
  terminalContext,
  ...hostProps
}: RightPanelWorkspaceSurfaceHostProps) {
  return (
    <BrowserWorkspaceSurfaceProvider value={browserContext}>
      <DiffWorkspaceSurfaceProvider value={diffContext}>
        <FilesWorkspaceSurfaceProvider value={filesContext}>
          <TerminalWorkspaceSurfaceProvider value={terminalContext}>
            <RightPanelSurfaceContentProvider value={surfaceContent}>
              <WorkspaceSurfaceHost
                {...hostProps}
                definitions={RIGHT_PANEL_WORKSPACE_SURFACE_DEFINITIONS}
              />
            </RightPanelSurfaceContentProvider>
          </TerminalWorkspaceSurfaceProvider>
        </FilesWorkspaceSurfaceProvider>
      </DiffWorkspaceSurfaceProvider>
    </BrowserWorkspaceSurfaceProvider>
  );
}
